import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";


const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;


const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
  
  const [paymentStatus, setPaymentStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [processingStep, setProcessingStep] = useState('Validating payment...');

  // Get authenticated API headers
  const getAuthHeaders = async () => {
    try {
      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'https://masho-api/'
        }
      });

      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      throw new Error('Authentication failed');
    }
  };

  // Validate PhonePe callback with backend
  const validatePhonePeCallback = async (callbackData) => {
    try {
      setProcessingStep('Validating payment with PhonePe...');
      
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/api/phonepe/validate-callback/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          authorization_header: callbackData.authHeader,
          callback_body: JSON.stringify(callbackData.allParams),
          merchant_order_id: callbackData.merchantOrderId
        }),
      });

      const result = await response.json();
      // console.log('PhonePe callback validation response:', result);
      
      if (response.ok && result.success) {
        return result;
      } else {
        throw new Error(result.error || 'Callback validation failed');
      }
    } catch (error) {
      console.error('Error validating PhonePe callback:', error);
      throw error;
    }
  };

  // Check order status with PhonePe API
  const checkOrderStatus = async (merchantOrderId) => {
    try {
      setProcessingStep('Checking order status...');
      
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/api/phonepe/order-status/${merchantOrderId}/`, {
        method: 'GET',
        headers,
      });

      const result = await response.json();
      // console.log('PhonePe order status response:', result);
      
      if (response.ok && result.success) {
        return result;
      } else {
        throw new Error(result.error || 'Order status check failed');
      }
    } catch (error) {
      console.error('Error checking order status:', error);
      throw error;
    }
  };

  // Update payment status using the actual API endpoint
  const updatePaymentStatus = async (orderId, paymentStatus, paymentDetails = {}) => {
    try {
      setProcessingStep('Updating payment status...');
      
      const headers = await getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/update-payment-status/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          payment_status: paymentStatus,
          payment_details: {
            transaction_id: paymentDetails.transaction_id || '',
            payment_method: paymentDetails.payment_method || 'phonepe',
            payment_date: new Date().toISOString(),
            merchant_order_id: paymentDetails.merchant_order_id || '',
            amount: paymentDetails.amount || 0,
            validation_result: paymentDetails.validation_result || {},
            order_status_result: paymentDetails.order_status_result || {},
            callback_data: paymentDetails.callback_data || {},
            ...paymentDetails
          }
        }),
      });

      const result = await response.json();
      // console.log('Payment status update response:', result);
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to update payment status');
      }
      
      return result;
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  };

  // Clear user cart after successful payment
  const clearUserCart = async () => {
    try {
      setProcessingStep('Clearing cart...');
      
      const headers = await getAuthHeaders();
      const userEmail = user?.email;
      
      if (!userEmail) {
        console.warn('No user email found, skipping cart clear');
        return;
      }

      const encodedEmail = encodeURIComponent(userEmail);
      const response = await fetch(`${API_BASE_URL}/api/cart/clear/${encodedEmail}/`, {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        console.log('Cart cleared successfully');
      } else {
        console.error('Failed to clear cart');
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      // Don't throw error here as it's not critical
    }
  };

  // Log payment callback data for debugging
  const logPaymentCallback = async (paymentData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/log-callback/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          callback_data: paymentData,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          referrer: document.referrer,
        }),
      });

      if (!response.ok) {
        console.error('Failed to log payment callback');
      }
    } catch (error) {
      console.error('Error logging payment callback:', error);
      // Don't throw error as logging is not critical
    }
  };

  // Main payment callback handler
  useEffect(() => {
    const handlePaymentCallback = async () => {
      try {
        // console.log('Payment callback initiated');
        
        // Check authentication first
        if (!isAuthenticated) {
          throw new Error('User not authenticated. Please log in and try again.');
        }

        // Extract URL parameters
        const callbackParams = {};
        for (const [key, value] of searchParams.entries()) {
          callbackParams[key] = value;
        }
        
        // console.log('Payment callback params:', callbackParams);
        
        // Get stored order data
        const storedOrderData = sessionStorage.getItem('pendingOrder');
        if (!storedOrderData) {
          throw new Error("Order data not found. Please contact support.");
        }

        const pendingOrder = JSON.parse(storedOrderData);
        setOrderData(pendingOrder);
        
        // Extract key callback data
        const merchantOrderId = pendingOrder.merchantOrderId || callbackParams.merchantOrderId;
        const authHeader = callbackParams.authorization || callbackParams['X-VERIFY'] || callbackParams.checksum;
        const callbackStatus = callbackParams.status || callbackParams.code;
        
        if (!merchantOrderId) {
          throw new Error('Merchant order ID not found in callback or stored data');
        }

        // Prepare callback data for logging
        const callbackData = {
          merchantOrderId,
          authHeader,
          allParams: callbackParams,
          storedOrder: pendingOrder,
          initialStatus: callbackStatus
        };

        // Log callback for debugging (non-blocking)
        await logPaymentCallback(callbackData);

        // Step 1: Validate callback if we have auth header
        let validationResult = null;
        if (authHeader) {
          try {
            validationResult = await validatePhonePeCallback(callbackData);
            // console.log('Callback validation successful:', validationResult);
          } catch (validationError) {
            console.warn('Callback validation failed:', validationError.message);
            // Continue without validation if it fails
          }
        }

        // Step 2: Check order status with PhonePe API
        let orderStatusResult = null;
        try {
          orderStatusResult = await checkOrderStatus(merchantOrderId);
          // console.log('Order status check result:', orderStatusResult);
        } catch (statusError) {
          console.warn('Order status check failed:', statusError.message);
          // Continue with URL parameter status if API check fails
        }

        // Step 3: Determine final payment status
        let finalStatus = callbackStatus?.toLowerCase() || 'unknown';
        
        // Priority: API status > Validation status > URL parameter status
        if (orderStatusResult?.state) {
          finalStatus = orderStatusResult.state.toLowerCase();
        } else if (validationResult?.state) {
          finalStatus = validationResult.state.toLowerCase();
        }

        // console.log('Final payment status:', finalStatus);

        // Step 4: Process based on payment status
        if (finalStatus === 'completed' || finalStatus === 'success' || finalStatus === 'paid') {
          await handleSuccessfulPayment(pendingOrder, callbackParams, {
            validationResult,
            orderStatusResult,
            merchantOrderId,
            finalStatus
          });
        } else {
          await handleFailedPayment(pendingOrder, callbackParams, {
            validationResult,
            orderStatusResult,
            merchantOrderId,
            finalStatus,
            errorMessage: getErrorMessage(finalStatus)
          });
        }
        
      } catch (error) {
        console.error("Payment callback error:", error);
        setError(error.message);
        setPaymentStatus('failed');
        
        // Navigate to failure page with error
        setTimeout(() => {
          navigate('/order-failure', {
            state: {
              orderId: searchParams.get('orderId') || 'Unknown',
              total: parseFloat(searchParams.get('total')) || 0,
              errorMessage: `Processing error: ${error.message}`,
              canRetry: false
            },
            replace: true
          });
        }, 3000);
      }
    };

    handlePaymentCallback();
  }, [searchParams, navigate, getAccessTokenSilently, isAuthenticated, user]);

  const handleSuccessfulPayment = async (pendingOrder, callbackParams, transactionDetails) => {
    try {
      setPaymentStatus('success');
      
      await updatePaymentStatus(
        pendingOrder.orderData.order_id,
        'paid',
        {
          transaction_id: callbackParams.transactionId || callbackParams.razorpay_payment_id || transactionDetails.orderStatusResult?.transaction_id,
          payment_method: 'phonepe',
          merchant_order_id: transactionDetails.merchantOrderId,
          amount: pendingOrder.finalTotal,
          validation_result: transactionDetails.validationResult,
          order_status_result: transactionDetails.orderStatusResult,
          callback_data: callbackParams
        }
      );

      // Clear user cart
      await clearUserCart();

      // Clear pending order from session
      sessionStorage.removeItem('pendingOrder');
      
      // Navigate to success page
      setTimeout(() => {
        navigate('/success', {
          state: {
            orderId: pendingOrder.orderData.order_id,
            total: pendingOrder.finalTotal,
            billingInfo: pendingOrder.billingInfo,
            order: pendingOrder.orderData,
            discount: pendingOrder.appliedDiscount || 0,
            transactionId: callbackParams.transactionId || transactionDetails.orderStatusResult?.transaction_id,
            paymentMethod: 'PhonePe'
          },
          replace: true
        });
      }, 2000);

    } catch (updateError) {
      console.error('Error in success handling:', updateError);
      
      // Even if backend update fails, show success with warning
      setTimeout(() => {
        navigate('/success', {
          state: {
            orderId: pendingOrder.orderData.order_id,
            total: pendingOrder.finalTotal,
            billingInfo: pendingOrder.billingInfo,
            order: pendingOrder.orderData,
            discount: pendingOrder.appliedDiscount || 0,
            transactionId: callbackParams.transactionId,
            paymentMethod: 'PhonePe',
            warning: 'Payment successful but order status update failed. Please contact support if you don\'t receive confirmation.'
          },
          replace: true
        });
      }, 2000);
    }
  };

  // Handle failed payment
  const handleFailedPayment = async (pendingOrder, callbackParams, transactionDetails) => {
    try {
      setPaymentStatus('failed');
      
      // Update payment status to failed using the correct API endpoint
      await updatePaymentStatus(
        pendingOrder.orderData.order_id,
        'failed',
        {
          payment_method: 'phonepe',
          merchant_order_id: transactionDetails.merchantOrderId,
          failure_reason: transactionDetails.errorMessage,
          validation_result: transactionDetails.validationResult,
          order_status_result: transactionDetails.orderStatusResult,
          callback_data: callbackParams
        }
      );
    } catch (updateError) {
      console.error('Error updating failed payment status:', updateError);
    }

    // Navigate to failure page
    setTimeout(() => {
      navigate('/order-failure', {
        state: {
          orderId: pendingOrder.orderData.order_id,
          total: pendingOrder.finalTotal,
          billingInfo: pendingOrder.billingInfo,
          products: pendingOrder.orderData.cart_items,
          discount: pendingOrder.appliedDiscount || 0,
          errorMessage: transactionDetails.errorMessage,
          canRetry: transactionDetails.finalStatus !== 'cancelled'
        },
        replace: true
      });
    }, 2000);
  };

  const getErrorMessage = (status) => {
    const errorMessages = {
      'failed': "Payment failed. Please check your payment method and try again.",
      'cancelled': "Payment was cancelled. You can try again when ready.",
      'error': "A payment processing error occurred. Please try again.",
      'pending': "Payment is still being processed. Please wait or contact support.",
      'expired': "Payment session expired. Please try again.",
      'timeout': "Payment timed out. Please try again.",
      'declined': "Payment was declined by your bank. Please try a different payment method.",
      'unknown': "Payment status could not be determined. Please contact support."
    };
    
    return errorMessages[status] || errorMessages['unknown'];
  };

  const handleRetryPayment = () => {
    navigate('/checkout', { replace: true });
  };

  const handleGoHome = () => {
    sessionStorage.removeItem('pendingOrder');
    navigate('/', { replace: true });
  };

  const handleContactSupport = () => {
    navigate('/contact');
  };

  // Processing state
  if (paymentStatus === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white py-8 px-4">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow-lg">
          <div className="animate-spin w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full mx-auto mb-6"></div>
          <h2 className="text-2xl font-semibold text-[#333333] mb-4" style={{ fontFamily: "serif" }}>
            Processing Payment
          </h2>
          <p className="text-gray-600 mb-4">
            {processingStep}
          </p>
          <div className="text-sm text-gray-500 space-y-1">
            <p>• Validating payment details</p>
            <p>• Confirming transaction status</p>
            <p>• Updating order information</p>
          </div>
          <div className="mt-4 text-xs text-gray-400">
            Please don't close this window...
          </div>
        </div>
      </div>
    );
  }

  // Success state (redirecting)
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white py-8 px-4">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow-lg">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h2 className="text-2xl font-semibold text-[#333333] mb-4" style={{ fontFamily: "serif" }}>
            Payment Successful!
          </h2>
          <p className="text-gray-600 mb-4">
            Your order has been confirmed and processed successfully.
          </p>
          <div className="animate-pulse text-sm text-gray-500">
            Redirecting to order confirmation...
          </div>
        </div>
      </div>
    );
  }

  // Failed state (with retry options)
  if (paymentStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white py-8 px-4">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow-lg">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-semibold text-[#333333] mb-4" style={{ fontFamily: "serif" }}>
            Payment Processing Failed
          </h2>
          <p className="text-gray-600 mb-6">
            {error || "We encountered an issue while processing your payment."}
          </p>
          <div className="space-y-3">
            <button
              onClick={handleRetryPayment}
              className="w-full bg-[#D4AF37] text-white py-3 px-6 rounded-lg hover:bg-[#B8941F] transition-colors font-medium"
            >
              Try Again
            </button>
            <button
              onClick={handleContactSupport}
              className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Contact Support
            </button>
            <button
              onClick={handleGoHome}
              className="w-full text-gray-500 py-2 hover:text-gray-700 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PaymentCallback;